import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, Phone, CheckCircle, AlertCircle, Loader, Truck, ChevronDown, ChevronRight } from 'lucide-react';
import { agentDashboardService } from '../../../services/agentDashboardService';

/**
 * ContainerShipmentsModal
 * Shows all shipments in a container grouped by Commune → Quartier → delivery_tasks
 * Displays counters per quartier (total/LIVRE/INCIDENT/restantes)
 * Opens container = creates delivery_tasks for all shipments without tasks
 */
export default function ContainerShipmentsModal({ container, agentEmployeeId, onClose, onSuccess }) {
  const [grouped, setGrouped] = useState({});
  const [totalShipments, setTotalShipments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [openError, setOpenError] = useState('');
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openResult, setOpenResult] = useState(null);
  
  // Accordion state: { commune: { quartier: boolean } }
  const [expandedCommunes, setExpandedCommunes] = useState({});
  const [expandedQuartiers, setExpandedQuartiers] = useState({});

  useEffect(() => {
    loadShipmentsGrouped();
  }, [container?.id]);

  const loadShipmentsGrouped = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await agentDashboardService?.getContainerShipmentsGroupedByCommuneQuartier(container?.id);
      setGrouped(data?.grouped || {});
      setTotalShipments(data?.totalShipments || 0);
      
      // Auto-expand all on first load
      const communes = {};
      const quartiers = {};
      Object?.keys(data?.grouped || {})?.forEach(commune => {
        communes[commune] = true;
        quartiers[commune] = {};
        Object?.keys(data?.grouped?.[commune] || {})?.forEach(quartier => {
          quartiers[commune][quartier] = true;
        });
      });
      setExpandedCommunes(communes);
      setExpandedQuartiers(quartiers);
    } catch (err) {
      console.error('Error loading grouped shipments:', err);
      setError('Erreur de chargement des expéditions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenContainer = async () => {
    try {
      setOpening(true);
      setOpenError('');

      // Open container and create delivery tasks
      const result = await agentDashboardService?.openAbidjanContainer(
        container?.id,
        agentEmployeeId
      );

      // Automatically assign delivery zones and route order
      await agentDashboardService?.assignDeliveryZonesAndOrder(container?.id);

      setOpenResult(result);
      setOpenSuccess(true);
      onSuccess?.();

      setTimeout(() => {
        onClose?.();
      }, 2000);
    } catch (err) {
      console.error('Error opening container:', err);
      setOpenError(err?.message || 'Erreur lors de l\'ouverture du conteneur');
    } finally {
      setOpening(false);
    }
  };

  const toggleCommune = (commune) => {
    setExpandedCommunes(prev => ({
      ...prev,
      [commune]: !prev?.[commune]
    }));
  };

  const toggleQuartier = (commune, quartier) => {
    setExpandedQuartiers(prev => ({
      ...prev,
      [commune]: {
        ...(prev?.[commune] || {}),
        [quartier]: !prev?.[commune]?.[quartier]
      }
    }));
  };

  const getDeliveryTaskStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Pas de tâche
        </span>
      );
    }

    const statusConfig = {
      'A_PREPARER': { color: 'bg-yellow-100 text-yellow-800', label: 'À préparer' },
      'EN_LIVRAISON': { color: 'bg-blue-100 text-blue-800', label: 'En livraison' },
      'LIVRE': { color: 'bg-green-100 text-green-800', label: 'Livré' },
      'INCIDENT': { color: 'bg-red-100 text-red-800', label: 'INCIDENT' },
      'PROBLEME': { color: 'bg-red-100 text-red-800', label: 'Problème' }
    };

    const config = statusConfig?.[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-green-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 rounded-lg p-2">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold">{container?.containerCode}</h2>
              <p className="text-sm text-green-100">
                {totalShipments} expédition(s) - Regroupement par Commune/Quartier
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success Result */}
        {openResult && (
          <div className="bg-green-50 border-b border-green-200 p-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-green-900">Conteneur ouvert avec succès</p>
                <p className="text-sm text-green-700 mt-1">{openResult?.message}</p>
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded p-2">
                    <p className="text-green-600 font-medium">Tâches créées</p>
                    <p className="text-2xl font-bold text-green-900">{openResult?.tasksCreated}</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-gray-600 font-medium">Déjà existantes</p>
                    <p className="text-2xl font-bold text-gray-900">{openResult?.tasksAlreadyExist}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {openError && (
          <div className="bg-red-50 border-b border-red-200 p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{openError}</p>
            </div>
          </div>
        )}

        {/* Grouped Shipments - Accordion View */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-green-600 animate-spin" />
            </div>
          ) : Object?.keys(grouped)?.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Aucune expédition dans ce conteneur</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Commune Level */}
              {Object?.keys(grouped)?.sort()?.map((commune) => {
                const isExpandedCommune = expandedCommunes?.[commune];
                const communeQuartiers = grouped?.[commune] || {};
                const communeTotalShipments = Object?.values(communeQuartiers)?.reduce(
                  (sum, q) => sum + q?.counters?.total, 0
                );

                return (
                  <div key={commune} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Commune Header */}
                    <button
                      onClick={() => toggleCommune(commune)}
                      className="w-full bg-blue-50 hover:bg-blue-100 p-4 flex items-center justify-between transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        {isExpandedCommune ? (
                          <ChevronDown className="w-5 h-5 text-blue-600" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-blue-600" />
                        )}
                        <MapPin className="w-5 h-5 text-blue-600" />
                        <span className="font-bold text-blue-900 text-left">{commune}</span>
                      </div>
                      <span className="text-sm font-medium text-blue-700">
                        {communeTotalShipments} livraison(s)
                      </span>
                    </button>

                    {/* Quartiers Level (only show if commune expanded) */}
                    {isExpandedCommune && (
                      <div className="bg-white">
                        {Object?.keys(communeQuartiers)?.sort()?.map((quartier) => {
                          const isExpandedQuartier = expandedQuartiers?.[commune]?.[quartier];
                          const quartierData = communeQuartiers?.[quartier];
                          const counters = quartierData?.counters;

                          return (
                            <div key={quartier} className="border-t border-gray-200">
                              {/* Quartier Header */}
                              <button
                                onClick={() => toggleQuartier(commune, quartier)}
                                className="w-full bg-gray-50 hover:bg-gray-100 p-3 flex items-center justify-between transition-colors"
                              >
                                <div className="flex items-center space-x-3">
                                  {isExpandedQuartier ? (
                                    <ChevronDown className="w-4 h-4 text-gray-600" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-600" />
                                  )}
                                  <span className="font-semibold text-gray-900 text-left">{quartier}</span>
                                </div>
                                
                                {/* Quartier Counters */}
                                <div className="flex items-center space-x-3 text-xs">
                                  <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded font-medium">
                                    Total: {counters?.total}
                                  </span>
                                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-medium">
                                    Livré: {counters?.LIVRE}
                                  </span>
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-medium">
                                    Incident: {counters?.INCIDENT}
                                  </span>
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded font-medium">
                                    Restantes: {counters?.remaining}
                                  </span>
                                </div>
                              </button>

                              {/* Shipments List (only show if quartier expanded) */}
                              {isExpandedQuartier && (
                                <div className="bg-white divide-y divide-gray-100">
                                  {quartierData?.shipments?.map((shipment) => (
                                    <div
                                      key={shipment?.id}
                                      className="p-4 hover:bg-gray-50 transition-colors"
                                    >
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                          {/* Sender Info */}
                                          <div className="mb-2 pb-2 border-b border-gray-100">
                                            <p className="text-xs text-gray-500 mb-1">Expéditeur</p>
                                            <div className="flex items-center space-x-2">
                                              <p className="font-medium text-gray-900">{shipment?.senderName}</p>
                                              <span className="text-gray-400">•</span>
                                              <p className="text-sm text-gray-600">{shipment?.senderCity}</p>
                                            </div>
                                          </div>

                                          {/* Receiver Info */}
                                          <div className="mb-2">
                                            <p className="text-xs text-gray-500 mb-1">Destinataire</p>
                                            <h3 className="font-semibold text-gray-900 mb-1">{shipment?.receiverName}</h3>
                                            <div className="flex items-center space-x-2 text-sm text-gray-700 mb-1">
                                              <Phone className="w-4 h-4 text-green-600" />
                                              <p className="font-medium">{shipment?.receiverPhone}</p>
                                            </div>
                                            <div className="flex items-start space-x-2 text-sm">
                                              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                              <p className="text-gray-700">{shipment?.receiverAddress}</p>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Task Status Badge */}
                                        <div className="text-right">
                                          {getDeliveryTaskStatusBadge(shipment?.deliveryTaskStatus)}
                                          {shipment?.hasDeliveryTask && (
                                            <div className="flex items-center space-x-1 text-xs text-green-600 mt-1">
                                              <CheckCircle className="w-3 h-3" />
                                              <span>Tâche créée</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Packages to Deliver */}
                                      {shipment?.description && (
                                        <div className="bg-blue-50 border border-blue-200 rounded p-2">
                                          <p className="text-xs font-medium text-blue-900 mb-1">Colis à livrer</p>
                                          <p className="text-xs text-blue-800">{shipment?.description}</p>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between space-x-3">
            <div className="text-sm text-gray-600">
              <p className="font-medium">
                Regroupement automatique par commune et quartier
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Fermer
              </button>
              <button
                onClick={handleOpenContainer}
                disabled={opening || totalShipments === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {opening ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Ouverture...</span>
                  </>
                ) : (
                  <>
                    <Truck className="w-4 h-4" />
                    <span>Ouvrir le conteneur</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}